// @ts-check
/**
 * Icon set (DS-12). Most shapes follow Lucide (ISC/MIT, see third-party/lucide).
 * Every icon is a 24×24 stroked SVG sized by CSS (16px by default), so an unsized
 * icon can never render at its intrinsic page size.
 */

/** @typedef {["path", string] | ["circle", number, number, number] | ["rect", number, number, number, number, number]} Shape */

/** @type {Record<string, Shape[]>} */
const shapes = {
  activity: [
    [
      "path",
      "M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2",
    ],
  ],
  alert: [
    [
      "path",
      "m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3",
    ],
    ["path", "M12 9v4"],
    ["path", "M12 17h.01"],
  ],
  "alert-circle": [
    ["circle", 12, 12, 10],
    ["path", "M12 8v4"],
    ["path", "M12 16h.01"],
  ],
  "arrow-up": [
    ["path", "m5 12 7-7 7 7"],
    ["path", "M12 19V5"],
  ],
  bell: [
    ["path", "M10.27 21a2 2 0 0 0 3.46 0"],
    [
      "path",
      "M3.26 15.33A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.67C19.41 13.96 18 12.5 18 8A6 6 0 0 0 6 8c0 4.5-1.41 5.96-2.74 7.33",
    ],
  ],
  board: [
    ["rect", 3, 3, 18, 18, 2],
    ["path", "M9 3v18"],
    ["path", "M15 3v18"],
  ],
  book: [
    [
      "path",
      "M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20",
    ],
  ],
  box: [
    [
      "path",
      "M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z",
    ],
    ["path", "m3.3 7 8.7 5 8.7-5"],
    ["path", "M12 22V12"],
  ],
  check: [["path", "M20 6 9 17l-5-5"]],
  "chevron-down": [["path", "m6 9 6 6 6-6"]],
  "chevron-left": [["path", "m15 18-6-6 6-6"]],
  "chevron-right": [["path", "m9 18 6-6-6-6"]],
  clock: [
    ["circle", 12, 12, 10],
    ["path", "M12 6v6l4 2"],
  ],
  code: [
    ["path", "m16 18 6-6-6-6"],
    ["path", "m8 6-6 6 6 6"],
  ],
  copy: [
    ["rect", 8, 8, 14, 14, 2],
    ["path", "M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"],
  ],
  ellipsis: [
    ["circle", 12, 12, 1],
    ["circle", 19, 12, 1],
    ["circle", 5, 12, 1],
  ],
  "external-link": [
    ["path", "M15 3h6v6"],
    ["path", "M10 14 21 3"],
    ["path", "M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"],
  ],
  file: [
    ["path", "M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"],
    ["path", "M14 2v4a2 2 0 0 0 2 2h4"],
  ],
  filter: [
    ["path", "M3 6h18"],
    ["path", "M7 12h10"],
    ["path", "M10 18h4"],
  ],
  folder: [
    [
      "path",
      "M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z",
    ],
  ],
  globe: [
    ["circle", 12, 12, 10],
    ["path", "M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"],
    ["path", "M2 12h20"],
  ],
  hourglass: [
    ["path", "M5 22h14"],
    ["path", "M5 2h14"],
    [
      "path",
      "M17 22v-4.17a2 2 0 0 0-.59-1.42L12 12l-4.41 4.41A2 2 0 0 0 7 17.83V22",
    ],
    [
      "path",
      "M7 2v4.17a2 2 0 0 0 .59 1.42L12 12l4.41-4.41A2 2 0 0 0 17 6.17V2",
    ],
  ],
  // Lucide "workflow": the Work graph view.
  graph: [
    ["rect", 3, 3, 8, 8, 2],
    ["path", "M7 11v4a2 2 0 0 0 2 2h4"],
    ["rect", 13, 13, 8, 8, 2],
  ],
  image: [
    ["rect", 3, 3, 18, 18, 2],
    ["circle", 9, 9, 2],
    ["path", "m21 15-3.09-3.09a2 2 0 0 0-2.82 0L6 21"],
  ],
  inbox: [
    ["path", "M22 12h-6l-2 3h-4l-2-3H2"],
    [
      "path",
      "M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z",
    ],
  ],
  info: [
    ["circle", 12, 12, 10],
    ["path", "M12 16v-4"],
    ["path", "M12 8h.01"],
  ],
  list: [
    ["path", "M3 6h.01"],
    ["path", "M3 12h.01"],
    ["path", "M3 18h.01"],
    ["path", "M8 6h13"],
    ["path", "M8 12h13"],
    ["path", "M8 18h13"],
  ],
  lock: [
    ["rect", 3, 11, 18, 11, 2],
    ["path", "M7 11V7a5 5 0 0 1 10 0v4"],
  ],
  "log-out": [
    ["path", "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"],
    ["path", "m16 17 5-5-5-5"],
    ["path", "M21 12H9"],
  ],
  menu: [
    ["path", "M4 6h16"],
    ["path", "M4 12h16"],
    ["path", "M4 18h16"],
  ],
  message: [
    ["path", "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"],
  ],
  moon: [["path", "M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"]],
  "panel-bottom": [
    ["rect", 3, 3, 18, 18, 2],
    ["path", "M3 15h18"],
  ],
  "panel-left": [
    ["rect", 3, 3, 18, 18, 2],
    ["path", "M9 3v18"],
  ],
  "panel-right": [
    ["rect", 3, 3, 18, 18, 2],
    ["path", "M15 3v18"],
  ],
  paperclip: [
    [
      "path",
      "m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48",
    ],
  ],
  plug: [
    ["path", "M12 22v-5"],
    ["path", "M9 8V2"],
    ["path", "M15 8V2"],
    ["path", "M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z"],
  ],
  plus: [
    ["path", "M5 12h14"],
    ["path", "M12 5v14"],
  ],
  refresh: [
    ["path", "M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"],
    ["path", "M21 3v5h-5"],
    ["path", "M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"],
    ["path", "M8 16H3v5"],
  ],
  search: [
    ["circle", 11, 11, 8],
    ["path", "m21 21-4.3-4.3"],
  ],
  settings: [
    [
      "path",
      "M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z",
    ],
    ["circle", 12, 12, 3],
  ],
  shield: [
    [
      "path",
      "M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z",
    ],
  ],
  sparkles: [
    ["path", "m12 3 1.9 5.8L20 11l-6.1 2.2L12 19l-1.9-5.8L4 11l6.1-2.2z"],
  ],
  square: [["rect", 6, 6, 12, 12, 2]],
  star: [
    [
      "path",
      "M11.53 2.3a.53.53 0 0 1 .95 0l2.31 4.68a2.12 2.12 0 0 0 1.6 1.16l5.16.76a.53.53 0 0 1 .3.9l-3.74 3.64a2.12 2.12 0 0 0-.61 1.88l.88 5.14a.53.53 0 0 1-.77.56l-4.62-2.43a2.12 2.12 0 0 0-1.97 0L6.4 21.01a.53.53 0 0 1-.77-.56l.88-5.14a2.12 2.12 0 0 0-.61-1.88L2.16 9.8a.53.53 0 0 1 .3-.91l5.16-.75a2.12 2.12 0 0 0 1.6-1.16z",
    ],
  ],
  sun: [
    ["circle", 12, 12, 4],
    ["path", "M12 2v2"],
    ["path", "M12 20v2"],
    ["path", "m4.93 4.93 1.41 1.41"],
    ["path", "m17.66 17.66 1.41 1.41"],
    ["path", "M2 12h2"],
    ["path", "M20 12h2"],
    ["path", "m6.34 17.66-1.41 1.41"],
    ["path", "m19.07 4.93-1.41 1.41"],
  ],
  terminal: [
    ["path", "m7 11 2-2-2-2"],
    ["path", "M11 13h4"],
    ["rect", 3, 3, 18, 18, 2],
  ],
  trash: [
    ["path", "M3 6h18"],
    ["path", "M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"],
    ["path", "M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"],
  ],
  user: [
    ["path", "M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"],
    ["circle", 12, 7, 4],
  ],
  users: [
    ["path", "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"],
    ["circle", 9, 7, 4],
    ["path", "M22 21v-2a4 4 0 0 0-3-3.87"],
    ["path", "M16 3.13a4 4 0 0 1 0 7.75"],
  ],
  x: [
    ["path", "M18 6 6 18"],
    ["path", "m6 6 12 12"],
  ],
};

export const iconNames = Object.freeze(Object.keys(shapes));

const NS = "http://www.w3.org/2000/svg";
/**
 * @param {string} name
 * @param {{ className?: string, label?: string }} [options] A label makes the icon meaningful
 *   to assistive technology; without one it is decorative.
 */
export function icon(name, { className = "", label } = {}) {
  const definition = shapes[name];
  if (!definition) throw new Error(`Unknown icon: ${name}`);
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("class", `ui-icon${className ? ` ${className}` : ""}`);
  svg.dataset.icon = name;
  if (label) {
    svg.setAttribute("role", "img");
    svg.setAttribute("aria-label", label);
  } else svg.setAttribute("aria-hidden", "true");
  for (const [kind, ...values] of definition) {
    const shape = document.createElementNS(NS, kind);
    if (kind === "path") shape.setAttribute("d", String(values[0]));
    else if (kind === "circle")
      for (const [index, name] of ["cx", "cy", "r"].entries())
        shape.setAttribute(name, String(values[index]));
    else
      for (const [index, name] of ["x", "y", "width", "height", "rx"].entries())
        shape.setAttribute(name, String(values[index]));
    svg.append(shape);
  }
  return svg;
}
